import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { blogService } from '../../services/blogService';
import { categoryService } from '../../services/categoryService';
import { tagService } from '../../services/tagService';
import { authorService } from '../../services/authorService';
import TipTapEditor from '../../components/TipTapEditor';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import SearchableSelect from '../../components/common/SearchableSelect';
import DateInput, { toDisplayDate, toIsoDate } from '../../components/common/DateInput';
import Loader from '../../components/Loader';
import DOMPurify from 'dompurify';

// Mirrors backend/src/modules/blog/blog.constants.js kept in sync manually
// since frontend/backend don't share a module; this is the single place on
// the admin side the 3-6 rule is defined, reused by both the inline picker
// error and the pre-submit guards below.
const RELATED_BLOGS_MIN = 3;
const RELATED_BLOGS_MAX = 6;
const RELATED_PAGE_SIZE = 20;

const getPublishErrors = (data) => {
   const missing = [];
   if (!data.slug.trim()) missing.push('slug');
   if (!data.metaTitle.trim()) missing.push('meta title');
   if (!data.metaDescription.trim()) missing.push('meta description');
   if (!data.authorId) missing.push('author');
   if (!data.tagIds.length) missing.push('at least one tag');
   if (!data.faqs.some((f) => f.question.trim() && f.answer.trim())) {
      missing.push('at least one FAQ');
   }
   return missing;
};

const getRelatedBlogsError = (count) =>
   count > 0 && (count < RELATED_BLOGS_MIN || count > RELATED_BLOGS_MAX)
      ? `Select ${RELATED_BLOGS_MIN}–${RELATED_BLOGS_MAX} related articles, or clear the selection to use automatic suggestions`
      : undefined;

const BlogForm = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const isEdit = !!id;

   const [loading, setLoading] = useState(isEdit);
   const [savingStatus, setSavingStatus] = useState(null); // 'DRAFT' | 'PUBLISHED' | null
   const [categories, setCategories] = useState([]);
   const [tags, setTags] = useState([]);
   const [authors, setAuthors] = useState([]);
   const [relatedBlogOptions, setRelatedBlogOptions] = useState([]);
   // Meta title mirrors the title until the user edits it separately
   const [metaTitleCustom, setMetaTitleCustom] = useState(false);
   const [formData, setFormData] = useState({
      title: '',
      slug: '',
      categoryIds: [],
      tagIds: [],
      authorId: '',
      content: '',
      status: 'DRAFT',
      metaTitle: '',
      metaDescription: '',
      coverImage: '',
      faqs: [],
      relatedBlogIds: [],
      publishDate: '',
   });
   const [coverImagePreview, setCoverImagePreview] = useState('');
   const [showPreview, setShowPreview] = useState(false);
   const autoSavedId = useRef(null);

   useEffect(() => {
      fetchCategories();
      fetchTags();
      fetchAuthors();
      if (isEdit) {
         fetchBlog();
      }
   }, [id]);

   const fetchBlog = async () => {
      try {
         setLoading(true);
         const response = await blogService.getById(id);
         const blog = response.data;
         // Extract relative path from coverImage URL for formData
         let coverImagePath = '';
         if (blog.coverImage) {
            try {
               const url = new URL(blog.coverImage);
               coverImagePath = url.pathname.replace('/uploads/', '');
            } catch (e) {
               // If it's already a relative path, use as is
               coverImagePath = blog.coverImage.replace(/^.*\/uploads\//, '');
            }
         }

         setFormData({
            title: blog.title || '',
            slug: blog.slug || '',
            categoryIds: blog.categories.map((c) => c.id) || [],
            tagIds: blog.tags.map((t) => t.id) || [],
            authorId: blog.author?.id || '',
            content: blog.content || '',
            status: blog.status || 'DRAFT',
            metaTitle: blog.metaTitle || '',
            metaDescription: blog.metaDescription || '',
            coverImage: coverImagePath,
            faqs: blog.faqs || [],
            relatedBlogIds: blog.relatedBlogIds || [],
            // Stored as UTC; the field shows the IST calendar date
            publishDate: toDisplayDate(
               new Date(new Date(blog.createdAt).getTime() + 5.5 * 60 * 60 * 1000).toISOString()
            ),
         });
         setRelatedBlogOptions(blog.relatedBlogs || []);
         setMetaTitleCustom(Boolean(blog.metaTitle) && blog.metaTitle !== blog.title);
         setCoverImagePreview(blog.coverImage || '');
      } catch (error) {
         console.error('Error fetching blog:', error);
         toast.error('Failed to load blog');
         navigate('/admin/blogs');
      } finally {
         setLoading(false);
      }
   };

   const fetchCategories = async () => {
      try {
         const response = await categoryService.getAll();
         setCategories(response.data);
      } catch (error) {
         console.error('Error fetching categories:', error);
      }
   };

   const fetchTags = async () => {
      try {
         const response = await tagService.getAll();
         setTags(response.data);
      } catch (error) {
         console.error('Error fetching tags:', error);
      }
   };

   const fetchAuthors = async () => {
      try {
         const response = await authorService.getActive();
         setAuthors(response.data);
      } catch (error) {
         console.error('Error fetching authors:', error);
      }
   };

   const handleCreateCategory = async (name) => {
      try {
         const response = await categoryService.create({ name });
         const newCategory = response.data;
         setCategories((prev) => [...prev, newCategory]);
         return newCategory;
      } catch (error) {
         console.error('Error creating category:', error);
         throw error;
      }
   };

   const handleCreateTag = async (name) => {
      try {
         const response = await tagService.create({ name });
         const newTag = response.data;
         setTags((prev) => [...prev, newTag]);
         return newTag;
      } catch (error) {
         console.error('Error creating tag:', error);
         throw error;
      }
   };

   const handleCategorySearch = async (query) => {
      try {
         const response = await categoryService.search(query);
         return response.data || [];
      } catch (error) {
         console.error('Error searching categories:', error);
         return [];
      }
   };

   const handleTagSearch = async (query) => {
      try {
         const response = await tagService.search(query);
         return response.data || [];
      } catch (error) {
         console.error('Error searching tags:', error);
         return [];
      }
   };

   const handleRelatedBlogSearch = async (query, page = 1) => {
      try {
         const response = await blogService.search(query, RELATED_PAGE_SIZE, page);
         const results = (response.data || []).filter((b) => String(b.id) !== String(id));
         // Merge into local options so chips/selection resolve correctly
         // SearchableSelect resolves selected items against the `options`
         // prop, not the live search results, so freshly-found blogs must
         // be added here before they can be selected.
         setRelatedBlogOptions((prev) => {
            const merged = [...prev, ...results];
            const seen = new Set();
            return merged.filter((o) => (seen.has(o.id) ? false : seen.add(o.id)));
         });
         return results;
      } catch (error) {
         console.error('Error searching blogs:', error);
         return [];
      }
   };

   const handleCoverImageUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
         const response = await blogService.uploadCoverImage(file);
         setFormData({ ...formData, coverImage: response.data.path });
         setCoverImagePreview(response.data.url);
      } catch (error) {
         console.error('Error uploading cover image:', error);
         toast.error('Failed to upload cover image');
      }
   };

   const handleImageUpload = async (file) => {
      try {
         const response = await blogService.uploadImage(file);
         return response.data.url;
      } catch (error) {
         console.error('Error uploading image:', error);
         throw error;
      }
   };

   const handleAddFaq = () => {
      if (formData.faqs.length >= 20) return;
      setFormData({ ...formData, faqs: [...formData.faqs, { question: '', answer: '' }] });
   };

   const handleRemoveFaq = (index) => {
      setFormData({ ...formData, faqs: formData.faqs.filter((_, i) => i !== index) });
   };

   const handleFaqChange = (index, field, value) => {
      const updated = formData.faqs.map((faq, i) =>
         i === index ? { ...faq, [field]: value } : faq
      );
      setFormData({ ...formData, faqs: updated });
   };

   const handleUpdate = async () => {
      if (
         !formData.title.trim() ||
         !formData.slug.trim() ||
         !formData.categoryIds.length ||
         !formData.content.trim()
      ) {
         toast.error('Please fill in all required fields');
         return;
      }

      if (formData.status === 'PUBLISHED') {
         const missing = getPublishErrors(formData);
         if (missing.length) {
            toast.error(`Required to publish: ${missing.join(', ')}`);
            return;
         }
      }

      const relatedBlogsError = getRelatedBlogsError(formData.relatedBlogIds.length);
      if (relatedBlogsError) {
         toast.error(relatedBlogsError);
         return;
      }

      try {
         setSavingStatus('UPDATE');
         await blogService.update(id, { ...formData, createdAt: toIsoDate(formData.publishDate) });
         toast.success('Blog updated successfully');
         navigate('/admin/blogs');
      } catch (error) {
         console.error('Error updating blog:', error);
         const errorMessage =
            error.response?.data?.errors?.map((e) => e.msg).join(', ') ||
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Failed to update blog';
         toast.error(errorMessage);
      } finally {
         setSavingStatus(null);
      }
   };

   const handleSubmit = async (status) => {
      if (
         !formData.title.trim() ||
         !formData.slug.trim() ||
         !formData.categoryIds.length ||
         !formData.content.trim()
      ) {
         toast.error('Please fill in all required fields');
         return;
      }

      if (status === 'PUBLISHED') {
         const missing = getPublishErrors(formData);
         if (missing.length) {
            toast.error(`Required to publish: ${missing.join(', ')}`);
            return;
         }
      }

      const relatedBlogsError = getRelatedBlogsError(formData.relatedBlogIds.length);
      if (relatedBlogsError) {
         toast.error(relatedBlogsError);
         return;
      }

      const dataToSave = { ...formData, status, createdAt: toIsoDate(formData.publishDate) };

      try {
         setSavingStatus(status);
         if (isEdit) {
            await blogService.update(id, dataToSave);
            toast.success(
               `Blog ${status === 'PUBLISHED' ? 'published' : 'saved as draft'} successfully`
            );
         } else if (autoSavedId.current) {
            await blogService.update(autoSavedId.current, dataToSave);
            toast.success(
               `Blog ${status === 'PUBLISHED' ? 'published' : 'saved as draft'} successfully`
            );
         } else {
            await blogService.create(dataToSave);
            toast.success(
               `Blog ${status === 'PUBLISHED' ? 'published' : 'saved as draft'} successfully`
            );
         }
         navigate('/admin/blogs');
      } catch (error) {
         console.error('Error saving blog:', error);
         const errorMessage =
            error.response?.data?.errors?.map((e) => e.msg).join(', ') ||
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Failed to save blog';
         toast.error(errorMessage);
      } finally {
         setSavingStatus(null);
      }
   };

   const handleAutoSave = useCallback(async () => {
      if (!formData.title.trim() || !formData.categoryIds.length) return;

      try {
         const effectiveId = id || autoSavedId.current || null;
         const response = await blogService.autoSave(effectiveId, formData);
         if (!effectiveId && response.data?.id) {
            autoSavedId.current = response.data.id;
         }
      } catch (error) {
         console.error('Error auto-saving:', error);
      }
   }, [formData, id]);

   // Auto-save every 30 seconds
   useEffect(() => {
      const interval = setInterval(handleAutoSave, 30000);
      return () => clearInterval(interval);
   }, [handleAutoSave]);

   const formatSlug = (text, stripEdges = false) => {
      let slug = text
         .toLowerCase()
         .replace(/\s+/g, '-')
         .replace(/[^a-z0-9-]/g, '')
         .replace(/-+/g, '-');
      if (stripEdges) {
         slug = slug.replace(/^-|-$/g, '');
      }
      return slug;
   };

   const handleSlugChange = (e) => {
      setFormData({ ...formData, slug: formatSlug(e.target.value) });
   };

   const handleAutoGenerateSlug = () => {
      if (!formData.title.trim()) return;
      setFormData({ ...formData, slug: formatSlug(formData.title, true) });
   };

   if (loading) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <div className="space-y-6">
            <form className="space-y-6">
               <div className="flex items-center justify-between">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                     {isEdit ? 'Edit Blog' : 'Create New Blog'}
                  </h1>
                  <div className="flex gap-3">
                     <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/admin/blogs')}
                        disabled={!!savingStatus}
                     >
                        Cancel
                     </Button>
                     {isEdit && (
                        <Button
                           type="button"
                           variant="primary"
                           onClick={handleUpdate}
                           disabled={!!savingStatus}
                        >
                           {savingStatus === 'UPDATE' ? 'Updating...' : 'Update'}
                        </Button>
                     )}
                     {(!isEdit || formData.status === 'PUBLISHED') && (
                        <Button
                           type="button"
                           variant="secondary"
                           onClick={() => handleSubmit('DRAFT')}
                           disabled={!!savingStatus}
                        >
                           {savingStatus === 'DRAFT' ? 'Saving...' : 'Save as Draft'}
                        </Button>
                     )}
                     {(!isEdit || formData.status === 'DRAFT') && (
                        <Button
                           type="button"
                           variant="success"
                           onClick={() => handleSubmit('PUBLISHED')}
                           disabled={!!savingStatus}
                        >
                           {savingStatus === 'PUBLISHED' ? 'Publishing...' : 'Publish'}
                        </Button>
                     )}
                  </div>
               </div>
               <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                     value={formData.title}
                     onChange={(e) => {
                        const title = e.target.value;
                        setFormData({
                           ...formData,
                           title,
                           ...(!metaTitleCustom && { metaTitle: title }),
                        });
                     }}
                     placeholder="Enter blog title"
                     required
                  />
               </div>

               <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Slug <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-start gap-3">
                     <div className="flex-1">
                        <Input
                           value={formData.slug}
                           onChange={handleSlugChange}
                           placeholder="enter-blog-slug"
                           className="!mb-0"
                        />
                     </div>
                     <Button type="button" variant="secondary" onClick={handleAutoGenerateSlug}>
                        Generate
                     </Button>
                  </div>
                  {formData.slug && (
                     <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        /{formData.slug}
                     </p>
                  )}
               </div>
               <div>
                  <div className="mb-4">
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Meta Title <span className="text-red-500">*</span>
                     </label>
                     <Input
                        value={formData.metaTitle}
                        onChange={(e) => {
                           const metaTitle = e.target.value;
                           // Clearing the field resumes mirroring the title
                           setMetaTitleCustom(metaTitle !== '');
                           setFormData({ ...formData, metaTitle });
                        }}
                        placeholder="SEO meta title"
                     />
                  </div>

                  <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Meta Description <span className="text-red-500">*</span>
                     </label>
                     <textarea
                        value={formData.metaDescription}
                        onChange={(e) =>
                           setFormData({ ...formData, metaDescription: e.target.value })
                        }
                        placeholder="SEO meta description"
                        rows="3"
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-secondary px-4 py-2 text-gray-900 dark:text-gray-100"
                     />
                  </div>
               </div>

               <div className="grid grid-cols-2 gap-[12px]">
                  <div>
                     <SearchableSelect
                        label="Categories"
                        required
                        multiple
                        placeholder="Search or create categories..."
                        options={categories}
                        value={formData.categoryIds}
                        onChange={(values) => {
                           setFormData({ ...formData, categoryIds: values });
                        }}
                        onSearch={handleCategorySearch}
                        onCreate={handleCreateCategory}
                        createText={(query) => `Create category: "${query}"`}
                     />
                  </div>

                  <div>
                     <SearchableSelect
                        label="Tags"
                        required
                        multiple
                        placeholder="Search or create tags..."
                        options={tags}
                        value={formData.tagIds}
                        onChange={(values, selectedItems) => {
                           setFormData({ ...formData, tagIds: values });
                        }}
                        onSearch={handleTagSearch}
                        onCreate={handleCreateTag}
                        createText={(query) => `Create tag: "${query}"`}
                     />
                  </div>
               </div>

               {/* Publish date */}
               <div>
                  <DateInput
                     label="Publish Date"
                     value={formData.publishDate}
                     onChange={(publishDate) => setFormData({ ...formData, publishDate })}
                  />
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                     Set a past date to back-date this post. Leave empty to use today.
                  </p>
               </div>

               {/* Author */}
               <div>
                  <SearchableSelect
                     label="Author"
                     required
                     searchable={false}
                     placeholder="No author"
                     options={[{ id: '', name: 'No author' }, ...authors]}
                     value={formData.authorId}
                     onChange={(value) =>
                        setFormData({ ...formData, authorId: value ? parseInt(value) : '' })
                     }
                     className="!mb-0"
                  />
                  {authors.length === 0 && (
                     <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                        No active authors found.{' '}
                        <a
                           href="/admin/authors/new"
                           className="text-blue-500 hover:underline"
                           target="_blank"
                           rel="noreferrer"
                        >
                           Create one
                        </a>
                        .
                     </p>
                  )}
               </div>

               {/* Related Articles */}
               <div>
                  <SearchableSelect
                     label="Related Articles"
                     multiple
                     placeholder="Search by title, category or tag..."
                     options={relatedBlogOptions}
                     value={formData.relatedBlogIds}
                     getOptionLabel={(o) => o.title}
                     onChange={(values) => setFormData({ ...formData, relatedBlogIds: values })}
                     onSearch={handleRelatedBlogSearch}
                     loadOnOpen
                     pageSize={RELATED_PAGE_SIZE}
                     error={getRelatedBlogsError(formData.relatedBlogIds.length)}
                     className="!mb-0"
                  />
                  <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                     {formData.relatedBlogIds.length} selected - leave empty for automatic
                     suggestions, or pick 3–6 to curate what shows in "Related Articles".
                  </p>
               </div>

               <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Cover Image
                  </label>
                  <input
                     type="file"
                     accept="image/*"
                     onChange={handleCoverImageUpload}
                     className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-dark-accent dark:file:text-white"
                  />
                  <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                     Recommended:{' '}
                     <span className="font-medium text-gray-500 dark:text-gray-400">
                        1200 × 630 px
                     </span>{' '}
                     , 16:9 ratio, WebP/JPG/PNG, max 2 MB.
                  </p>
                  {coverImagePreview && (
                     <img
                        src={coverImagePreview}
                        alt="Cover preview"
                        className="mt-4 max-w-xs rounded-lg"
                     />
                  )}
               </div>

               <div>
                  <div className="flex items-center justify-between mb-2">
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Content <span className="text-red-500">*</span>
                     </label>
                     <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                        <button
                           type="button"
                           onClick={() => setShowPreview(false)}
                           className={`px-3 py-1 text-sm font-medium transition-colors ${
                              !showPreview
                                 ? 'bg-blue-500 text-white'
                                 : 'bg-white dark:bg-dark-secondary text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                           }`}
                        >
                           Editor
                        </button>
                        <button
                           type="button"
                           onClick={() => setShowPreview(true)}
                           className={`px-3 py-1 text-sm font-medium transition-colors ${
                              showPreview
                                 ? 'bg-blue-500 text-white'
                                 : 'bg-white dark:bg-dark-secondary text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                           }`}
                        >
                           Preview
                        </button>
                     </div>
                  </div>

                  {showPreview ? (
                     <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-secondary min-h-[400px]">
                        {/* Blog Preview - matches website layout */}
                        <div className="px-6 py-8 max-w-4xl mx-auto">
                           {/* Title */}
                           <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                              {formData.title || 'Untitled Blog Post'}
                           </h1>

                           {/* Meta info */}
                           <div className="flex items-center gap-3 mb-4">
                              {formData.categoryIds.length > 0 && (
                                 <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                    {formData.categoryIds
                                       .map((id) => categories.find((c) => c.id === id)?.name)
                                       .filter(Boolean)
                                       .join(', ')}
                                 </span>
                              )}
                              <span className="text-gray-400 dark:text-gray-500">&bull;</span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                 {new Date().toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                 })}
                              </span>
                           </div>

                           {/* Tags */}
                           {formData.tagIds.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-6">
                                 {formData.tagIds.map((tagId) => {
                                    const tag = tags.find((t) => t.id === tagId);
                                    return tag ? (
                                       <span
                                          key={tagId}
                                          className="text-sm px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                                       >
                                          {tag.name}
                                       </span>
                                    ) : null;
                                 })}
                              </div>
                           )}

                           {/* Cover Image */}
                           {coverImagePreview && (
                              <div className="relative w-full h-96 mb-8 rounded-lg overflow-hidden">
                                 <img
                                    src={coverImagePreview}
                                    alt={formData.title}
                                    className="w-full h-full object-cover"
                                 />
                              </div>
                           )}

                           {/* Content */}
                           <div
                              className="blog-preview-content prose prose-lg dark:prose-invert max-w-none prose-gray dark:text-white"
                              dangerouslySetInnerHTML={{
                                 __html: DOMPurify.sanitize(formData.content || '', {
                                    ADD_TAGS: ['mark', 'sup', 'sub', 'input', 'label', 'iframe'],
                                    ADD_ATTR: [
                                       'style',
                                       'data-checked',
                                       'data-type',
                                       'type',
                                       'checked',
                                       'disabled',
                                       'data-color',
                                       'src',
                                       'width',
                                       'height',
                                       'allowfullscreen',
                                       'frameborder',
                                       'allow',
                                    ],
                                    ALLOW_DATA_ATTR: true,
                                 }),
                              }}
                           />
                        </div>
                     </div>
                  ) : (
                     <TipTapEditor
                        content={formData.content}
                        onChange={(content) => setFormData({ ...formData, content })}
                        onImageUpload={handleImageUpload}
                     />
                  )}
               </div>

               {/* FAQ Section */}
               <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                     <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                           FAQs <span className="text-red-500">*</span>
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                           Required to publish. Shown in Google search results as expandable answers
                        </p>
                     </div>
                     <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formData.faqs.length} / 20
                     </span>
                  </div>

                  <div className="space-y-4">
                     {formData.faqs.map((faq, index) => (
                        <div
                           key={index}
                           className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3"
                        >
                           <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                 FAQ #{index + 1}
                              </span>
                              <button
                                 type="button"
                                 onClick={() => handleRemoveFaq(index)}
                                 className="text-sm text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              >
                                 Remove
                              </button>
                           </div>
                           <div>
                              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                 Question
                              </label>
                              <Input
                                 value={faq.question}
                                 onChange={(e) =>
                                    handleFaqChange(index, 'question', e.target.value)
                                 }
                                 placeholder="e.g. What is this article about?"
                              />
                           </div>
                           <div>
                              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                 Answer
                              </label>
                              <textarea
                                 value={faq.answer}
                                 onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                                 placeholder="Write a clear, concise answer..."
                                 rows="3"
                                 className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-secondary px-4 py-2 text-sm text-gray-900 dark:text-gray-100"
                              />
                           </div>
                        </div>
                     ))}
                  </div>

                  {formData.faqs.length < 20 && (
                     <button
                        type="button"
                        onClick={handleAddFaq}
                        className="mt-4 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                     >
                        + Add FAQ
                     </button>
                  )}
               </div>
            </form>
         </div>
      </PanelLayout>
   );
};

export default BlogForm;
